import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { ColorLens, Functions, HighlightAlt } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSnackbar } from "notistack";

import { TreeEnvironmentRef, TreeItem, TreeItemIndex } from "ui/Tree";

import { getUserPreferenceValue, selectAllVersionGroupConfigs, selectVersionConfig, useGetBroadbandProvidersQuery } from "fond/api";
import { cbgLayerFilterOptions, fccLayerFilterOptions, OverlayIds, subareaFilterOptions, SubareaLayerId } from "fond/cityPlanner/configuration";
import { densityLayerFilterOptions } from "fond/cityPlanner/configuration/densityOverlayConfiguration";
import { soilLayerFilterOptions } from "fond/cityPlanner/configuration/soilOverlayConfiguration";
import { LayerFilterContext } from "fond/cityPlanner/LayerFilterContext";
import { getFilteredProviders } from "fond/cityPlanner/Map/useMultiProjectConfig";
import { useFeatureFlag } from "fond/featureFlags";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { commentGroupConfig } from "fond/layers";
import { convertAndFormatLegendCount } from "fond/layers/functions";
import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { BlockSpinner, LegendBadge, LegendButton, Total } from "fond/map/Legend/Legend.styles";
import { MapContext } from "fond/map/MapProvider";
import useFeatureTotals from "fond/map/useFeatureTotals";
import { useMapBounds } from "fond/map/useMapBounds";
import { EditMode, getCurrentProject, getCurrentProjectView, setEditMode, setLayerVisibility } from "fond/project/redux";
import { clearSelection, selectSelectionState } from "fond/redux/selection";
import { GroupConfig, MapLayerConfig, Store, UserPreferenceKey, UserPreferenceSubKey } from "fond/types";
import { LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { pickIDs } from "fond/utils";
import { generateTreeItems, isVisible } from "fond/utils/configurations";
import { CTS_GROUPCONFIG_ID } from "fond/utils/costToServeTransformers";
import { useAppDispatch } from "fond/utils/hooks";
import { Actions } from "fond/utils/permissions";
import { canCustomiseStyles, isMultiProject } from "fond/utils/project";

import DataCitations from "./Adornments/DataCitations";
import { LayerFilterOption } from "./Adornments/LayerFilter";
import { LayerFilter } from "./Adornments";
import BaseLegend from "./BaseLegend";
import LegendWarning from "./LegendWarning";
import SelectionSummary from "./SelectionSummary";

export type LegendItemType = MapLayerConfig | LayerConfig | SublayerConfig | LayerStyle | GroupConfig;

const LEGEND_TREE_ID = "legendTreeId";

const Legend: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const environmentRef = useRef<TreeEnvironmentRef>();
  const versionId = useSelector((state: Store) => state.project.versionId);
  const projectId = useSelector((state: Store) => state.project.projectId);
  const config = useSelector((state: Store) => selectVersionConfig(state, versionId));
  const groupConfigs = useSelector((state: Store) => selectAllVersionGroupConfigs(state, versionId));
  const layerView = useSelector((state: Store) => getCurrentProjectView(state.project)?.layers ?? {});
  const permissionLevel = useSelector((state: Store) => getCurrentProject(state.project)?.Permission.Level);
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const systemOfMeasurement = project?.SystemOfMeasurement ?? "imperial";
  const selection = useSelector(selectSelectionState);
  const { featureTotals, loadLayerFeatureTotals, isLoadingFeatureTotals, summaryTotals } = useFeatureTotals({
    versionId,
    project,
    selectionGeometry: selection.status === "selected" ? selection.polygon.geometry : undefined,
  });
  const canEditStyles = usePermissionCheck(Actions.PROJECT_EDIT_STYLES, permissionLevel);
  const projectSupportsCustomisedStyles = canCustomiseStyles(project);
  const { value: isSelectEnabled } = useFeatureFlag("legend_selection");
  const canSelect = isSelectEnabled && !isMultiProject(project);
  const showStyleEditorNewBadge = useSelector((state: Store) =>
    getUserPreferenceValue(state, UserPreferenceKey.UI_TOUR, UserPreferenceSubKey.STYLE_EDITOR)
  );
  const [showTotals, setShowTotals] = useState<boolean>(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const editMode = useSelector((state: Store) => state.project.editMode);
  const mapContext = useContext(MapContext);
  const isDrawing = mapContext?.isDrawing ?? false;

  // Disable the feature-selection button when the user is in another draw mode, or another tool owns the draw control
  const isSelectDisabled = (editMode !== "none" && editMode !== "select") || (isDrawing && selection.status !== "drawing");
  const selectTooltip = { none: "Select features by polygon", drawing: "Cancel selection", selected: "Clear selection" }[selection.status];

  /**
   * The Select button cycles the selection: start drawing, cancel an
   * in-progress draw, or clear a completed selection.
   */
  const handleOnSelectClick = () => {
    if (selection.status === "selected") {
      dispatch(clearSelection());
    } else {
      dispatch(setEditMode(selection.status === "drawing" ? EditMode.none : EditMode.select));
    }
  };

  const isEntityVisible = useCallback((key: string) => isVisible(config, { id: key, layerView }), [config, layerView]);
  const { filters, onChange } = useContext(LayerFilterContext);

  const onChangeLayerFilter = useCallback(
    (layerId: string, childId: string, optionIds: string[], multiple: boolean, show: boolean) => {
      // When a layer is added via the filter, force it to become visible. This prevents a situation where
      // the user made a layer invisible, then removed it, and then added it back in again, and it's still invisible.
      if (show && projectId) {
        dispatch(setLayerVisibility(projectId, [childId], true));
      }

      onChange(layerId, childId, optionIds, multiple, show);
    },
    [onChange, dispatch, projectId]
  );

  /**
   * Broadband providers handling
   */
  const { bounds: mapBounds } = useMapBounds({ skip: !isMultiProject(project) });
  const accountId = project?.Account.ID;
  const { data: broadbandProviders } = useGetBroadbandProvidersQuery(
    accountId && mapBounds && isMultiProject(project) ? { accountId, bounds: mapBounds } : skipToken
  );

  /**
   * Generate the data source for the tree based on the collection of configuration sources
   */
  const treeItems = useMemo(() => generateTreeItems({ config, showStyles: false }), [config]);

  /**
   * On expanding items we load the feature totals for layers
   */
  const handleOnExpand = useCallback(
    (item: TreeItem<LegendItemType>) => {
      if (!showTotals || item.data?.Type !== "LAYER") return;
      loadLayerFeatureTotals(item.data);
    },
    [loadLayerFeatureTotals, showTotals]
  );

  /**
   * Monitor showTotals & if items are expanded we need to request their totals
   */
  useEffect(() => {
    if (showTotals) {
      environmentRef.current?.viewState?.[LEGEND_TREE_ID]?.expandedItems?.forEach((id) => {
        const treeItem = environmentRef.current?.items[id];
        if (treeItem) handleOnExpand(treeItem);
      });
    }
  }, [showTotals, handleOnExpand]);

  /**
   * Returns the TreeItem's title value based on the LegendItemType
   */
  const getItemTitle = useCallback(({ data: itemData }: TreeItem<LegendItemType>) => {
    if (itemData.Type === "STYLE") return itemData.Name;
    if (itemData.Type === "MapLayerConfig") return "";
    return itemData.Label;
  }, []);

  const getLayerFilter = useCallback(
    (args: {
      id: OverlayIds.FCC | OverlayIds.DEMOGRAPHICS | OverlayIds.DENSITIES | OverlayIds.SOIL | SubareaLayerId.ROOT;
      options: LayerFilterOption[];
      multiple: boolean;
    }) => {
      return <LayerFilter filteredLayerIds={filters[args.id]} onChange={onChangeLayerFilter} {...args} />;
    },
    [filters, onChangeLayerFilter]
  );

  /**
   * Allow selection of broadband providers
   */
  const currentFccLayerFilterOptions = useMemo(() => {
    // Keep track of the providers to display in our filter. The big three are always included.
    const providers = getFilteredProviders(filters);

    // Include any broadband providers that have coverage in the current map bounds.
    (broadbandProviders ?? []).forEach((provider) => providers.add(provider.Name));

    // Construct the autocomplete options by sorting broadband providers alphabetically
    const providerOptions = Array.from(providers)
      .sort()
      .map((providerName) => {
        const id = `fcc-${providerName}-service-types`;
        return { ID: id, Label: providerName };
      });

    // Create the autocomplete for broadband providers
    const options = [
      {
        ID: "Coverage",
        Label: "Coverage",
        Type: "Category" as const,
      },
      {
        ID: "fcc-broadband-providers",
        Label: "Coverage",
        Type: "Autocomplete" as const,
        Options: providerOptions,
      },
    ];
    return [...fccLayerFilterOptions, ...options];
  }, [broadbandProviders, filters]);

  const renderItemLeftAdornment = useCallback(
    ({ data }: TreeItem<LegendItemType>) => {
      if (data.Type !== "GROUP" && data.Type !== "MapLayerConfig") {
        return <InferredIcon entity={data as LayerConfig | SublayerConfig | LayerStyle} config={config} />;
      }

      return null;
    },
    [config]
  );

  const renderItemFilter = useCallback(
    (props: TreeItem<LegendItemType>) => {
      if (props.data?.ID === OverlayIds.FCC) {
        return getLayerFilter({ id: OverlayIds.FCC, options: currentFccLayerFilterOptions, multiple: true });
      }

      if (props.data?.ID === OverlayIds.DEMOGRAPHICS) {
        return getLayerFilter({ id: OverlayIds.DEMOGRAPHICS, options: cbgLayerFilterOptions, multiple: true });
      }

      if (props.data?.ID === OverlayIds.DENSITIES) {
        return getLayerFilter({ id: OverlayIds.DENSITIES, options: densityLayerFilterOptions, multiple: true });
      }

      if (props.data?.ID === OverlayIds.SOIL) {
        return getLayerFilter({ id: OverlayIds.SOIL, options: soilLayerFilterOptions, multiple: true });
      }

      if (props.data?.ID === SubareaLayerId.ROOT) {
        return getLayerFilter({ id: SubareaLayerId.ROOT, options: subareaFilterOptions, multiple: false });
      }

      return null;
    },
    [currentFccLayerFilterOptions, getLayerFilter]
  );

  const handleCopy = useCallback(
    async (content: string, id: string) => {
      try {
        if (content) {
          await navigator.clipboard.writeText(content);
          setCopiedId(id);
        }
      } catch (e) {
        enqueueSnackbar("Failed to copy feature count to clipboard.", { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const renderItemRightAdornment = useCallback(
    (props: TreeItem<LegendItemType>) => {
      let formattedTotal = null;
      let showItemTotals = showTotals && ["LAYER", "SUBLAYER", "CommentConfig"].includes(props.data.Type);

      if (showItemTotals && featureTotals) {
        const { Type, ID } = props.data;
        let total = null;
        if (Type === "LAYER") {
          total = ID in featureTotals ? featureTotals[ID] : (featureTotals[props.data.Key] ?? null);
        } else if (["SUBLAYER", "CommentConfig"].includes(Type)) {
          total = featureTotals[ID] ?? null;
        }
        formattedTotal = total !== null ? convertAndFormatLegendCount(total, systemOfMeasurement) : null;
      }

      return (
        <>
          {showItemTotals && !formattedTotal && isLoadingFeatureTotals && <BlockSpinner />}
          {formattedTotal && (
            <Tooltip title={copiedId === props.data.ID ? "Copied!" : "Copy to clipboard"}>
              <Total
                data-testid="row-total"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(formattedTotal, props.data.ID);
                }}
                onMouseEnter={() => {
                  if (copiedId === props.data.ID) setCopiedId(null);
                }}
              >
                {`(${formattedTotal})`}
              </Total>
            </Tooltip>
          )}
          {renderItemFilter(props)}
          {props.data.ID === "multiProjectOverlayGroup" && <DataCitations />}
        </>
      );
    },
    [copiedId, showTotals, featureTotals, handleCopy, renderItemFilter, systemOfMeasurement, isLoadingFeatureTotals]
  );

  const onItemVisibilityChanges = useCallback(
    (itemsToShow: TreeItemIndex[], itemsToHide: TreeItemIndex[]) => {
      if (itemsToShow.length > 0) {
        dispatch(setLayerVisibility(projectId, itemsToShow, true));
      }
      if (itemsToHide.length > 0) {
        dispatch(setLayerVisibility(projectId, itemsToHide, false));
      }
    },
    [dispatch, projectId]
  );

  const onItemExpand = useCallback((item: TreeItem<LegendItemType>) => handleOnExpand(item), [handleOnExpand]);
  const defaultExpanded = useMemo(() => [CTS_GROUPCONFIG_ID, ...pickIDs(groupConfigs), commentGroupConfig.ID], [groupConfigs]);
  const expand = useMemo(() => ({ onItemExpand, defaultExpanded }), [onItemExpand, defaultExpanded]);

  return (
    <BaseLegend
      environmentRef={environmentRef}
      treeId={LEGEND_TREE_ID}
      treeItems={treeItems}
      getItemTitle={getItemTitle}
      getItemTitleClass={(item) => `rct-tree-item-button-${item.data?.Type}`}
      getItemClass={(item) => `rct-tree-item-title-${item.data?.Type}`}
      getItemVisibility={(item) => isEntityVisible(item as string)}
      renderItemLeftAdornment={renderItemLeftAdornment}
      renderItemRightAdornment={renderItemRightAdornment}
      onItemVisibilityChanges={onItemVisibilityChanges}
      expand={expand}
      buttons={
        <>
          <Tooltip title={`${showTotals ? "Hide" : "Show"} layer totals`} PopperProps={{ disablePortal: true }}>
            <LegendButton
              variant="outlined"
              size="small"
              startIcon={<Functions />}
              onClick={() => setShowTotals(!showTotals)}
              data-testid="toggle-totals-button"
              isActive={showTotals}
              sx={{ mr: 0.5 }}
            >
              Show totals
            </LegendButton>
          </Tooltip>
          {canSelect && (
            <Tooltip title={selectTooltip} PopperProps={{ disablePortal: true }}>
              <span>
                <LegendButton
                  variant="outlined"
                  size="small"
                  startIcon={<HighlightAlt />}
                  disabled={isSelectDisabled}
                  isActive={selection.status !== "none"}
                  onClick={handleOnSelectClick}
                  data-testid="select-button"
                  sx={{ mr: 0.5 }}
                >
                  Select
                </LegendButton>
              </span>
            </Tooltip>
          )}
          {projectSupportsCustomisedStyles && (
            <LegendBadge
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent="new"
              color="primary"
              invisible={!showStyleEditorNewBadge}
              data-testid="style-editor-new-badge"
            >
              <LegendButton
                variant="outlined"
                disabled={!canEditStyles}
                size="small"
                startIcon={<ColorLens />}
                onClick={() => navigate(`/styles/${versionId}`)}
                data-testid="edit-styles-button"
              >
                Edit styles
              </LegendButton>
            </LegendBadge>
          )}
        </>
      }
      stickyHeader={!isMultiProject(project) && <SelectionSummary summary={summaryTotals} systemOfMeasurement={systemOfMeasurement} />}
      header={!isMultiProject(project) && <LegendWarning versionId={versionId} />}
    />
  );
};

export default Legend;
